import { Fragment, useCallback, useEffect, useState } from 'react';
import type { Api, PasswordLinkResult, UserInfo, UserKillResult } from '../api';
import { ROLE, ROLE_LABEL, USER_STATUS } from '../admin/labels';
import { fmtDateTime } from '../admin/format';
import { PermissionPanel } from './PermissionPanel';
import { FeatureChecks } from './FeatureChecks';
import { PasswordLinkNotice } from './PasswordLinkNotice';
import { ALL_FEATURES, describeFeatures, normalizeFeatures } from '../features';
import { confirmText } from '../ui/confirm';
import { EMAIL_CHECK_REQUIRED_MESSAGE } from '../emailRules';
import { EmailCheckField } from './EmailCheckField';
import { useEmailCheck } from './useEmailCheck';
import { EmailAssignPanel } from './EmailAssignPanel';
import { SessionLimitInput } from './SessionLimitInput';
import { SESSION_LIMIT_HINT, parseSessionLimit, sessionLimitInput, sessionLimitText } from '../admin/sessionLimit';

/**
 * 한 사용자 행 아래에 펼치는 작업 패널 종류 — 한 번에 하나만 열린다("지금 무엇을 고치는 중인지" 가 분명하게).
 * `email` = 새 이메일 지정(§14.19.10 — "새 이메일 지정 필요" 사용자만).
 */
type Panel = 'edit' | 'permissions' | 'email';

/**
 * 추가 폼의 빈 값. 이메일 = 로그인 ID(§14.19). 기능 기본 = 전부(§14.19.2).
 * 비밀번호 칸은 없다(§14.19.9, 2026-09-28) — 본인이 초대 메일 링크로 정한다.
 */
const EMPTY_FORM = { email: '', userName: '', role: ROLE.USER as string, features: [...ALL_FEATURES] as string[] };

/** 링크 발급 결과 안내(누구에게 무엇을). */
type LinkNotice = { who: string; email: string; result: PasswordLinkResult };

/** 관리자 행의 기능 칸 안내 — 관리자는 기능 권한 편집 대상이 아니다(늘 전부). */
const ADMIN_FEATURE_NOTE = '관리자는 모든 기능을 씁니다.';

/** 화면에 보일 이메일 — "새 이메일 지정 필요" 사용자는 임시 주소 대신 원래 주소(§14.19.10). */
const displayEmail = (u: UserInfo): string => (u.emailReassignRequired && u.originalEmail ? u.originalEmail : u.email);

/** 저장된 기능(옛 서버는 필드가 없다 → 전부). */
const featuresOf = (u: UserInfo): string[] => normalizeFeatures(u.features ?? ALL_FEATURES);

/**
 * 사용자 관리(2026-09-15 관리 화면, 관리자 전용). 서버 `/api/users`.
 *
 * - 추가(초대 메일) / 이름·역할 변경 / 초대 다시 보내기·재설정 메일 / 비활성화·재활성화 / 삭제 / 기기 권한
 * - **본인 행**은 역할 변경·비활성화·삭제를 막는다(서버도 409 로 막는다 — 조직에서 관리자가 사라지지 않게)
 * - 비활성화는 그 사람의 **모든 로그인과 진행 중인 원격 세션을 끊는다** — 결과(끊은 건수)를 그대로 알린다
 * - 🔴 관리자는 **비밀번호를 정해 주지 않는다**(§14.19.9, 2026-09-28 사용자 결정) — 본인이 메일 링크로 정한다.
 *   메일이 안 나가면 링크를 보여 주고 관리자가 직접 전달한다(`PasswordLinkNotice`).
 *
 * @param meId 로그인한 관리자 본인의 userId(본인 행 판정)
 */
export function UsersView({ api, meId }: { api: Api; meId: string }) {
  const [users, setUsers] = useState<UserInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [linkNotice, setLinkNotice] = useState<LinkNotice | null>(null);
  const [busyId, setBusyId] = useState('');

  // 행별 작업 패널
  const [openId, setOpenId] = useState('');
  const [panel, setPanel] = useState<Panel | null>(null);
  const [editName, setEditName] = useState('');
  const [editRole, setEditRole] = useState<string>(ROLE.USER);
  const [editFeatures, setEditFeatures] = useState<string[]>([...ALL_FEATURES]);
  // 동시 세션 상한(§15) — 입력 글자(빈 칸 = 무제한). 관리자에게도 걸 수 있다(역할과 무관한 운영 값).
  const [editLimit, setEditLimit] = useState('');

  // 추가 폼
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  // 이메일 형식 + [중복 확인](2026-09-29) — 확인을 통과해야 [추가] 가 켜진다.
  const emailCheck = useEmailCheck(api, form.email);
  const resetEmailCheck = emailCheck.reset;
  const closeAdd = useCallback(() => {
    setAdding(false);
    setForm(EMPTY_FORM);
    resetEmailCheck();
  }, [resetEmailCheck]);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setUsers(await api.listUsers());
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const closePanel = useCallback(() => {
    setOpenId('');
    setPanel(null);
  }, []);

  /** 패널 열기 — 같은 패널을 다시 누르면 닫는다(토글). */
  const openPanel = useCallback(
    (u: UserInfo, p: Panel) => {
      if (openId === u.userId && panel === p) {
        closePanel();
        return;
      }
      setOpenId(u.userId);
      setPanel(p);
      setEditName(u.userName);
      setEditRole(u.role);
      setEditFeatures(featuresOf(u));
      setEditLimit(sessionLimitInput(u.maxConcurrentSessions));
      setError('');
      setNotice('');
      setLinkNotice(null);
    },
    [closePanel, openId, panel],
  );

  /** 행 단위 조작의 공통 껍데기 — 행 잠금 → 실행 → 알림 → 목록 재조회 → 오류 노출(기기 화면과 같은 흐름). */
  const runRow = useCallback(
    async (userId: string, action: () => Promise<string>) => {
      setBusyId(userId);
      setError('');
      setNotice('');
      setLinkNotice(null);
      try {
        setNotice(await action());
        await refresh();
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setBusyId('');
      }
    },
    [refresh],
  );

  /** 끊은 것들을 사람이 읽는 문장으로. 0건 항목은 뺀다(0 을 나열하면 무엇이 일어났는지 흐려진다). */
  const describeKill = (r: UserKillResult): string => {
    const parts = [
      r.revokedLogins > 0 ? `로그인 ${r.revokedLogins}건 종료` : '',
      r.closedSessions > 0 ? `원격 세션 ${r.closedSessions}건 종료` : '',
      r.disconnected ? '열려 있던 창 연결 종료' : '',
    ].filter(Boolean);
    return parts.length ? ` (${parts.join(' · ')})` : '';
  };

  const submitAdd = useCallback(async () => {
    const email = form.email.trim();
    const userName = form.userName.trim();
    if (!email || !userName) {
      setError('이메일과 이름을 입력하세요.');
      return;
    }
    // 형식·중복은 [중복 확인] 이 통과시킨 값만(버튼이 꺼져 있어 보통은 여기 안 온다 — 다른 경로 방어).
    if (!emailCheck.view.confirmed) {
      emailCheck.touch();
      setError(emailCheck.view.formatOk ? EMAIL_CHECK_REQUIRED_MESSAGE : emailCheck.view.message || '이메일 형식이 아닙니다.');
      return;
    }
    await runRow('__add__', async () => {
      // 관리자는 기능 권한과 무관(늘 전부) — 보내지 않아 서버 기본값(전부)으로 둔다. 강등되면 그 값이 적용된다.
      // 비밀번호는 보내지 않는다 → 서버가 "설정 대기" 로 만들고 초대 메일을 보낸다(§14.19.9).
      const created = await api.createUser({
        email,
        userName,
        role: form.role,
        ...(form.role === ROLE.USER ? { features: form.features } : {}),
      });
      closeAdd();
      if (created.invite) {
        setLinkNotice({ who: userName, email: created.email, result: created.invite });
        return `'${userName}' 사용자를 추가했습니다.`;
      }
      // 옛 서버(초대 미지원) — 비밀번호 없이 만들 수 없어 여기 오지 않는다(서버가 400). 방어적으로만 남긴다.
      return `'${userName}' 사용자를 추가했습니다.`;
    });
  }, [api, closeAdd, emailCheck, form, runRow]);

  /**
   * 초대 다시 보내기(설정 대기) · 재설정 메일(비밀번호 있음) — §14.19.9.
   * 재설정은 본인이 링크로 새 비밀번호를 정할 때까지 **지금 비밀번호가 그대로 유효**하다(관리자가 바꾸지 않는다).
   * 이전에 보낸 링크는 무효가 된다 — 그래서 재설정 쪽만 확인을 받는다(초대는 아직 쓸 수 없는 계정이라 부담이 없다).
   */
  const sendLink = useCallback(
    async (u: UserInfo) => {
      if (!u.passwordPending) {
        const ok = await confirmText(
          `'${u.userName}(${u.email})' 에게 비밀번호 재설정 메일을 보낼까요?\n\n` +
            `본인이 메일의 링크에서 새 비밀번호를 정하면 기존 로그인이 모두 끊깁니다.\n` +
            `그 전까지는 지금 비밀번호로 계속 로그인할 수 있습니다. 링크는 1시간 동안 유효합니다.`,
          '보내기',
          false, // 차단·삭제가 아니다 — 위험 색을 쓰지 않는다
        );
        if (!ok) return;
      }
      await runRow(u.userId, async () => {
        const result = await api.sendPasswordLink(u.userId);
        setLinkNotice({ who: u.userName, email: u.email, result });
        return '';
      });
    },
    [api, runRow],
  );

  const submitEdit = useCallback(
    async (u: UserInfo) => {
      const name = editName.trim();
      if (!name) {
        setError('이름은 비워 둘 수 없습니다.');
        return;
      }
      const limit = parseSessionLimit(editLimit);
      if (!limit.ok) {
        setError(limit.error);
        return;
      }
      const patch: { userName?: string; role?: string; features?: string[]; maxConcurrentSessions?: number | null } = {};
      if (name !== u.userName) patch.userName = name;
      if (editRole !== u.role) patch.role = editRole;
      // 기능은 **일반 사용자로 남는(또는 되는) 경우에만** 보낸다 — 관리자 칸은 잠겨 있어 값이 의미 없다.
      const nextFeatures = normalizeFeatures(editFeatures);
      if (editRole === ROLE.USER && nextFeatures.join(',') !== featuresOf(u).join(',')) {
        patch.features = nextFeatures;
      }
      // 옛 서버(필드 없음 = undefined)는 무제한으로 본다 — 빈 칸 그대로면 보내지 않는다.
      if (limit.value !== (u.maxConcurrentSessions ?? null)) patch.maxConcurrentSessions = limit.value;
      if (!patch.userName && !patch.role && !patch.features && patch.maxConcurrentSessions === undefined) {
        closePanel();
        return;
      }
      await runRow(u.userId, async () => {
        await api.updateUser(u.userId, patch);
        closePanel();
        const said: string[] = [];
        if (patch.userName) said.push(`이름을 '${name}' 으로`);
        if (patch.role) said.push(`역할을 ${ROLE_LABEL[patch.role] ?? patch.role}(으)로`);
        if (patch.features) said.push(`기능을 '${describeFeatures(patch.features)}' 로`);
        if (patch.maxConcurrentSessions !== undefined) {
          said.push(`동시 세션 상한을 ${sessionLimitText(patch.maxConcurrentSessions)}(으)로`);
        }
        // 역할은 즉시(다음 요청부터), 기능·상한은 다음 세션부터 — 진행 중 세션은 그대로다.
        const when =
          patch.features || patch.maxConcurrentSessions !== undefined
            ? ' 기능·상한 변경은 다음 접속부터 적용됩니다.'
            : patch.role
              ? ' 즉시 적용됩니다.'
              : '';
        return `'${name}' — ${said.join(', ')} 바꿨습니다.${when}`;
      });
    },
    [api, closePanel, editFeatures, editLimit, editName, editRole, runRow],
  );

  /** 새 이메일 지정(§14.19.10) — 지정 뒤 [다시 활성화] 를 눌러야 쓸 수 있다(서버가 지정 전 재활성화를 막는다). */
  const submitEmail = useCallback(
    async (u: UserInfo, email: string) => {
      await runRow(u.userId, async () => {
        await api.updateUser(u.userId, { email });
        closePanel();
        return `'${u.userName}' 의 이메일을 '${email}' 로 지정했습니다. [다시 활성화] 하면 이 주소로 로그인할 수 있습니다.`;
      });
    },
    [api, closePanel, runRow],
  );

  /**
   * 비활성화/재활성화. **비활성화**가 파급이 큰 쪽이라 그쪽에서 확인을 받는다(기기 차단과 같은 방향).
   */
  const toggleDisabled = useCallback(
    async (u: UserInfo) => {
      const disable = u.status === USER_STATUS.ACTIVE;
      if (disable) {
        const ok = await confirmText(
          `'${u.userName}(${u.email})' 사용자를 비활성화할까요?\n\n` +
            `로그인이 막히고, 지금 열려 있는 창과 진행 중인 원격 세션이 즉시 끊깁니다.\n` +
            `기록과 기기 권한은 그대로 남으며, 언제든 다시 활성화할 수 있습니다.`,
        );
        if (!ok) return;
      }
      await runRow(u.userId, async () => {
        const r = await api.setUserDisabled(u.userId, disable);
        return disable
          ? `'${u.userName}' 을(를) 비활성화했습니다${describeKill(r)}.`
          : `'${u.userName}' 을(를) 다시 활성화했습니다. 다시 로그인하면 사용할 수 있습니다.`;
      });
    },
    [api, runRow],
  );

  const remove = useCallback(
    async (u: UserInfo) => {
      const ok = await confirmText(
        `'${u.userName}(${displayEmail(u)})' 사용자를 삭제할까요? 되돌릴 수 없습니다.\n\n` +
          `기기 권한과 주소록이 함께 지워지고, 접속 기록에는 '(삭제된 사용자)' 로 남습니다.\n` +
          `나중에 다시 쓸 수도 있다면 삭제 대신 '비활성화' 를 권합니다.`,
      );
      if (!ok) return;
      await runRow(u.userId, async () => {
        await api.deleteUser(u.userId);
        if (openId === u.userId) closePanel();
        return `'${u.userName}' 사용자를 삭제했습니다.`;
      });
    },
    [api, closePanel, openId, runRow],
  );

  return (
    <div className="view">
      <div className="row" style={{ justifyContent: 'space-between', marginBottom: 16 }}>
        <h2 style={{ margin: 0 }}>사용자</h2>
        <div className="row">
          <button onClick={() => (adding ? closeAdd() : setAdding(true))}>{adding ? '추가 닫기' : '사용자 추가'}</button>
          <button className="ghost" onClick={refresh} disabled={loading}>
            {loading ? '불러오는 중…' : '새로고침'}
          </button>
        </div>
      </div>

      {adding && (
        <div className="enroll-panel">
          <div className="name" style={{ marginBottom: 10 }}>새 사용자</div>
          <div className="form-grid">
            <label className="field field-wide">
              <span>이메일(로그인 ID · 기기 등록 요청을 받을 주소)</span>
              <EmailCheckField
                value={form.email}
                onChange={(email) => setForm({ ...form, email })}
                emailCheck={emailCheck}
                placeholder="name@example.com"
              />
            </label>
            <label className="field">
              <span>이름</span>
              <input value={form.userName} onChange={(e) => setForm({ ...form, userName: e.target.value })} maxLength={100} />
            </label>
            <label className="field">
              <span>역할</span>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                <option value={ROLE.USER}>{ROLE_LABEL[ROLE.USER]} — 권한을 받은 기기만</option>
                <option value={ROLE.ADMIN}>{ROLE_LABEL[ROLE.ADMIN]} — 모든 기기 + 관리</option>
              </select>
            </label>
          </div>
          <div className="feature-label">허용 기능</div>
          <FeatureChecks
            value={form.features}
            onChange={(features) => setForm({ ...form, features })}
            disabled={form.role !== ROLE.USER}
            lockedNote={ADMIN_FEATURE_NOTE}
          />
          <div className="hint" style={{ marginBottom: 10 }}>
            추가하면 이 이메일로 <b>초대 메일</b>이 갑니다. 본인이 메일의 링크에서 비밀번호를 직접 정한 뒤 로그인합니다
            (링크 72시간 유효).
          </div>
          <div className="row">
            <button onClick={() => void submitAdd()} disabled={busyId === '__add__' || !emailCheck.view.confirmed}>
              {busyId === '__add__' ? '추가 중…' : '추가'}
            </button>
            <button className="ghost" onClick={closeAdd}>
              취소
            </button>
            {!emailCheck.view.confirmed && <span className="hint">이메일 [중복 확인] 후 추가할 수 있습니다.</span>}
          </div>
        </div>
      )}

      {error && <div className="hint" style={{ color: 'var(--err)', marginBottom: 12 }}>{error}</div>}
      {notice && <div className="hint" style={{ marginBottom: 12 }}>{notice}</div>}
      {linkNotice && <PasswordLinkNotice {...linkNotice} onClose={() => setLinkNotice(null)} />}

      {users.length === 0 && !loading ? (
        <p className="hint">사용자가 없습니다.</p>
      ) : (
        <div className="list">
          {users.map((u) => {
            const self = u.userId === meId;
            const disabled = u.status !== USER_STATUS.ACTIVE;
            const busy = busyId === u.userId;
            const isOpen = openId === u.userId;
            // 새 이메일 지정 필요(§14.19.10) — 임시 주소 대신 원래 주소를 보이고, 재활성화 대신 [이메일 지정].
            const reassign = !!u.emailReassignRequired;
            const shownEmail = reassign ? `${displayEmail(u)} (다른 계정이 사용 중)` : u.email;
            return (
              <Fragment key={u.userId}>
                <div className="list-item" style={{ cursor: 'default', ...(disabled ? { opacity: 0.6 } : {}) }}>
                  <span className={`dot ${disabled ? 'err' : 'ok'}`} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="name">
                      {u.userName}
                      {u.role === ROLE.ADMIN && <span className="badge brand" style={{ marginLeft: 8 }}>관리자</span>}
                      {self && <span className="badge" style={{ marginLeft: 6 }}>나</span>}
                      {disabled && <span className="badge err" style={{ marginLeft: 6 }}>비활성</span>}
                      {u.passwordPending && (
                        <span className="badge warn" style={{ marginLeft: 6 }} title="초대 메일의 링크로 아직 비밀번호를 정하지 않았습니다">
                          설정 대기
                        </span>
                      )}
                      {reassign && (
                        <span className="badge warn" style={{ marginLeft: 6 }} title="조직을 되살렸지만 원래 이메일을 다른 계정이 쓰고 있습니다">
                          새 이메일 지정 필요
                        </span>
                      )}
                    </div>
                    <div className="sub">
                      {shownEmail} · {u.passwordPending ? '초대 메일 링크로 비밀번호를 정하면 로그인할 수 있습니다' : `마지막 로그인 ${fmtDateTime(u.lastLoginAt)}`}
                      {/* 일반 사용자만 — 관리자는 늘 전부라 적지 않는다. 전부일 때도 적지 않아 "좁혀 둔 사람" 이 눈에 띄게. */}
                      {u.role === ROLE.USER && featuresOf(u).length < ALL_FEATURES.length && (
                        <> · 기능 {describeFeatures(featuresOf(u))}</>
                      )}
                      {/* 상한을 둔 사람만 적는다(무제한이 기본 — 늘 적으면 "좁혀 둔 사람" 이 묻힌다). */}
                      {u.maxConcurrentSessions != null && <> · 동시 세션 상한 {u.maxConcurrentSessions}</>}
                    </div>
                  </div>
                  <div className="actions">
                    <button className={`ghost ${isOpen && panel === 'permissions' ? 'on' : ''}`} onClick={() => openPanel(u, 'permissions')} disabled={busy}>
                      기기 권한
                    </button>
                    <button className={`ghost ${isOpen && panel === 'edit' ? 'on' : ''}`} onClick={() => openPanel(u, 'edit')} disabled={busy}>
                      편집
                    </button>
                    {/* 비밀번호는 본인이 메일 링크로 정한다(§14.19.9). 비활성 계정은 링크를 받아도 쓸 수 없어 숨긴다. */}
                    {!disabled && (
                      <button className="ghost" onClick={() => void sendLink(u)} disabled={busy}>
                        {u.passwordPending ? '초대 다시 보내기' : '재설정 메일'}
                      </button>
                    )}
                    {/* 본인은 비활성화·삭제할 수 없다 — 버튼을 두고 409 를 받게 하는 것보다 처음부터 없는 편이 분명하다. */}
                    {!self && (
                      <>
                        {reassign ? (
                          <button className={`ghost ${isOpen && panel === 'email' ? 'on' : ''}`} onClick={() => openPanel(u, 'email')} disabled={busy}>
                            이메일 지정
                          </button>
                        ) : (
                          <button className="ghost" onClick={() => void toggleDisabled(u)} disabled={busy}>
                            {busy ? '처리 중…' : disabled ? '다시 활성화' : '비활성화'}
                          </button>
                        )}
                        <button className="ghost danger" onClick={() => void remove(u)} disabled={busy}>
                          삭제
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {isOpen && panel && (
                  <div className="subpanel">
                    {panel === 'edit' && (
                      <>
                      <div className="row" style={{ flexWrap: 'wrap' }}>
                        <input
                          autoFocus
                          value={editName}
                          maxLength={100}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') void submitEdit(u);
                            if (e.key === 'Escape') closePanel();
                          }}
                          aria-label="이름"
                        />
                        <select value={editRole} onChange={(e) => setEditRole(e.target.value)} disabled={self} aria-label="역할">
                          <option value={ROLE.USER}>{ROLE_LABEL[ROLE.USER]}</option>
                          <option value={ROLE.ADMIN}>{ROLE_LABEL[ROLE.ADMIN]}</option>
                        </select>
                        <button onClick={() => void submitEdit(u)} disabled={busy}>저장</button>
                        <button className="ghost" onClick={closePanel}>취소</button>
                        {self && <span className="hint">본인의 역할은 다른 관리자만 바꿀 수 있습니다.</span>}
                      </div>
                      <div className="feature-label" style={{ marginTop: 10 }}>허용 기능 (다음 접속부터 적용)</div>
                      <FeatureChecks
                        value={editFeatures}
                        onChange={setEditFeatures}
                        disabled={editRole !== ROLE.USER}
                        lockedNote={ADMIN_FEATURE_NOTE}
                      />
                      <div className="feature-label" style={{ marginTop: 10 }}>동시 세션 상한</div>
                      <div className="row" style={{ flexWrap: 'wrap' }}>
                        <SessionLimitInput
                          value={editLimit}
                          onChange={setEditLimit}
                          onEnter={() => void submitEdit(u)}
                          onEscape={closePanel}
                        />
                        <span className="hint">{SESSION_LIMIT_HINT} 조직 상한이 있으면 둘 다 적용됩니다.</span>
                      </div>
                      </>
                    )}
                    {panel === 'permissions' && <PermissionPanel api={api} user={u} />}
                    {panel === 'email' && (
                      <EmailAssignPanel
                        api={api}
                        user={u}
                        busy={busy}
                        onSubmit={(email) => void submitEmail(u, email)}
                        onCancel={closePanel}
                      />
                    )}
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
